import SHDImage from '@ui/Image/SHDImage';
import SHDSelectable from '@ui/Selectable/SHDSelectable';
import SHDSwiper from '@ui/Swiper/SHDSwiper';
import SHDText from '@ui/Text/SHDText';
import SHDToggle from '@ui/ToggleButton/SHDToggleButton';
import { Box, ResponsiveContext } from 'grommet';
import { useContext } from 'react';
import SHDLoader from '@ui/Loader/SHDLoader';
import {
  ScoreboardPosition,
  useOverlaySettings,
} from '@hooks/useOverlaySettings';
import { useStreamStylingTracking } from '@hooks/useEventsStreamStylingSession';
import { getImageName } from '@helpers/utils/teamUtils';

export const StreamStyling = () => {
  const screenSize = useContext(ResponsiveContext);

  const {
    position,
    flipped,
    stylingType,
    setPosition,
    setFlipped,
    setStylingType,
    isLoading,
    isFlipDisabled,
    overlayEnabled,
  } = useOverlaySettings();

  useStreamStylingTracking({
    stylingType,
    position,
    flipped,
  });

  const rawPositions: (ScoreboardPosition | false)[] =
    stylingType === 'minimal' && overlayEnabled
      ? ['top', 'bottom']
      : [
          'top',
          'bottom',
          overlayEnabled && 'bottom-left',
          overlayEnabled && 'top-right',
        ];

  const items = (rawPositions.filter(Boolean) as ScoreboardPosition[]).map(
    (id) => ({
      id,
      content: (
        <Box gap="small">
          <SHDImage
            src={`../stream_settings/stream_styling/${id}${stylingType === 'minimal' ? '-minimal' : ''}-${position === id ? 'selected' : 'unselected'}.png`}
            alt={`${id} Preview`}
            width="auto"
          />
          <SHDText
            color={position === id ? 'green' : 'black'}
            textAlign="center"
            style={position === id ? 'body-semibold' : 'body'}
          >
            {id.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
          </SHDText>
        </Box>
      ),
    })
  );

  if (isLoading) {
    return (
      <Box pad="medium" align="center">
        <SHDLoader />
      </Box>
    );
  }

  return (
    <Box gap="medium">
      <Box
        pad={{ top: 'large', horizontal: 'medium' }}
        width="auto"
        alignSelf="start"
        direction="row"
        gap="small"
      >
        <SHDSelectable
          id="default"
          isActive={stylingType === 'default'}
          onSelected={() => {
            setStylingType('default');
            setPosition('top');
            setFlipped(false);
          }}
          round="20px"
          pad={{ horizontal: 'medium', vertical: 'small' }}
        >
          <SHDText
            style={stylingType === 'default' ? 'body-semibold' : 'body'}
            color={stylingType === 'default' ? 'green' : 'black'}
          >
            Default
          </SHDText>
        </SHDSelectable>

        {overlayEnabled && (
          <SHDSelectable
            id="minimal"
            isActive={stylingType === 'minimal'}
            onSelected={() => {
              setStylingType('minimal');
              setPosition('top');
              setFlipped(false);
            }}
            round="20px"
            pad={{ horizontal: 'medium', vertical: 'small' }}
          >
            <SHDText
              style={stylingType === 'minimal' ? 'body-semibold' : 'body'}
              color={stylingType === 'minimal' ? 'green' : 'black'}
            >
              Minimal
            </SHDText>
          </SHDSelectable>
        )}
      </Box>
      <Box pad={{ horizontal: screenSize === 'small' ? 'none' : 'medium' }}>
        <SHDImage
          round={screenSize === 'small' ? '' : '8px'}
          src={`../stream_settings/stream_styling/preview-image-${getImageName(stylingType, position, flipped)}.png`}
          alt="Bottom Preview"
          width={screenSize === 'small' ? 'auto' : 600}
        />
      </Box>
      <Box gap="small" pad={'medium'}>
        <SHDText style="subhead-bold">Scoreboard Position</SHDText>
        <SHDSwiper
          selectable={false}
          defaultSelectedId={position}
          onClick={(id) => {
            setPosition(id as ScoreboardPosition);
          }}
          items={items}
          pad="none"
        />
        <Box
          direction="row"
          justify="between"
          align="center"
          fill="horizontal"
          pad={{ top: 'large' }}
          style={{ maxWidth: 600 }}
        >
          <SHDText
            style="subhead-bold"
            color={isFlipDisabled ? 'gray' : 'black'}
          >
            Flip player card horizontally
          </SHDText>
          <SHDToggle
            isOn={flipped}
            disabled={isFlipDisabled}
            onToggle={() => {
              if (isFlipDisabled) {
                return;
              }
              setFlipped(!flipped);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};
